import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { IMG } from "./data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#090B0E] pt-28">
      <div className="blueprint-grid absolute inset-0 opacity-60" />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7 lg:pt-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-blue-400">
            <span className="h-2 w-2 animate-pulse bg-blue-500" /> Design-Build · Commercial + Residential · Northern NM
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[13vw] font-black uppercase leading-[0.85] tracking-tight text-white sm:text-[9.5vw] lg:text-[7rem]">
            <MaskLines lines={["We build", <span key="o" className="text-stroke whitespace-nowrap">with precision</span>, <span key="e" className="text-blue-500">& detail.</span>]} delay={0.35} />
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }} className="mt-10 max-w-xl">
            <p className="text-base leading-relaxed text-slate-300 sm:text-lg">
              Bennett Custom Builders is a design-build contractor serving all of Northern New Mexico — roofing, remodels and commercial projects built with structural precision and honest schedules.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#contact" data-testid="hero-quote-button" className="btn-primary">Request a Quote <ArrowUpRight className="h-4 w-4" /></a>
              <a href="#projects" data-testid="hero-portfolio-button" className="btn-ghost">View Projects <ArrowDownRight className="h-4 w-4" /></a>
            </div>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="clip-corner relative h-[60vh] min-h-[380px] overflow-hidden lg:h-[72vh]"
          >
            <motion.img style={{ y: imgY, scale: 1.2 }} src={IMG.hero} alt="A home built by Bennett Custom Builders" className="absolute inset-0 h-full w-full object-cover grayscale-[35%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090B0E] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-end font-mono text-[11px] uppercase tracking-[0.2em] text-slate-200" style={{ transform: "translateZ(60px)" }}>
              <span className="text-blue-400">● Roofing & remodels</span>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-6 -left-3 z-20 w-40 bg-white p-2.5 shadow-2xl sm:-left-8 sm:w-52"
          >
            <img src="/images/oc-preferred.jpg" alt="Owens Corning Preferred Contractor" className="w-full" />
          </motion.div>
          <div className="pointer-events-none absolute -left-3 -top-3 h-10 w-10 border-l-2 border-t-2 border-blue-600" />
          <div className="pointer-events-none absolute -bottom-3 -right-3 h-10 w-10 border-b-2 border-r-2 border-blue-600" />
        </div>
      </div>
    </section>
  );
};
